import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, CircularProgress, Divider, Stack, TextField, Typography } from '@mui/material';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import BlockIcon from '@mui/icons-material/Block';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import { extractErrorMessage, resources } from '../../core/services';
import { GlassPanel } from '../../shared/components/GlassPanel';
import { IconTile } from '../../shared/components/IconTile';
import { Pill } from '../../shared/components/Pill';
import { SoftButton } from '../../shared/components/SoftButton';
import type { SemanticColor } from '../../shared/components/semanticColor';
import { GenerationProgressPanel } from '../projects/dashboard/GenerationProgressPanel';
import { EntityDetailsDialog } from '../entities/EntityDetailsDialog';
import { KeyEntitiesRadarRow } from '../entities/project-dashboard/KeyEntitiesRadarRow';
import type {
  Entity,
  Job,
  ProjectValidationSummary,
  ProjectValidationSummaryData,
  ValidationDecision,
  ValidationRecommendation,
} from '../../core/types/data-models';
import type { ValidationStage } from './validationStages';
import type { ValidationCopy } from './validationCopy';
import {
  CompetitionBlock,
  CustomerBlock,
  DealBlock,
  EconomicsBlock,
  ExperimentsBlock,
  GtmBlock,
  MarketBlock,
  MetricsBlock,
  RisksBlock,
  ScorecardBlock,
  StockBlock,
  StrengthsBlock,
  TractionBlock,
  type StageLinks,
} from './summary/SummaryBlocks';
import { ScoreRing } from './summary/SummaryCharts';

const RECOMMENDATION_ICON: Record<ValidationRecommendation, ReactNode> = {
  GO: <RocketLaunchOutlinedIcon sx={{ fontSize: 15 }} />,
  CONDITIONAL_GO: <FlagOutlinedIcon sx={{ fontSize: 15 }} />,
  PIVOT: <AltRouteIcon sx={{ fontSize: 15 }} />,
  NO_GO: <BlockIcon sx={{ fontSize: 15 }} />,
};

const CONFIDENCE_LABEL = { HIGH: 'High confidence', MEDIUM: 'Medium confidence', LOW: 'Low confidence' } as const;

const DECISIONS: { key: ValidationDecision; icon: ReactNode }[] = [
  { key: 'GO', icon: <RocketLaunchOutlinedIcon /> },
  { key: 'PIVOT', icon: <AltRouteIcon /> },
  { key: 'NO_GO', icon: <BlockIcon /> },
];

// The note saves on its own once typing pauses; the decision saves at once.
const NOTE_SAVE_DELAY_MS = 800;

interface ValidationSummaryProps {
  projectId: number;
  stages: ValidationStage[];
  /** The wording of the project type: go / no-go for an idea, invest / pass for a business. */
  copy: ValidationCopy;
  /** The project's summary row; null until generated, undefined while loading. */
  summary: ProjectValidationSummary | null | undefined;
  /** The running GENERATE_VALIDATION_SUMMARY job, if any. */
  summaryJob: Job | null;
  /** The project's PENDING/PROCESSING jobs — the key entities and market radar read their runs off them. */
  activeJobs: Job[];
  /** End with the project's entity types and market radar (an idea validation has no overview to show them). */
  showProjectInsights: boolean;
  canEdit: boolean;
  /** True while any job runs on the project or a start is in flight — generating is not offered then. */
  busy: boolean;
  onGenerate: () => void;
  /** Shows the given stage's document in place of the summary. */
  onOpenStage: (docType: string) => void;
}

/**
 * The last step: everything needed for the decision on one screen, from
 * the project's summary row. A verdict with its score, confidence and
 * conditions; the scorecard of the stages; what supports the case and what
 * could kill it; the key numbers; the stage digests the documents support
 * (for a business also its traction and the deal frame, for a stock the
 * price frame); the checks to run
 * next; and the recorded decision.
 *
 * Before the row exists the panel offers to build it; while the job runs it
 * shows the progress (over the previous summary, when there is one); once a
 * stage was regenerated after the summary it says so and offers an update.
 * A built summary can always be regenerated, e.g. after a stage was re-scored.
 * With `showProjectInsights` it ends with the project's entity types and its
 * market radar, laid out as on the project dashboard.
 */
export function ValidationSummary({
  projectId,
  stages,
  copy,
  summary,
  summaryJob,
  activeJobs,
  showProjectInsights,
  canEdit,
  busy,
  onGenerate,
  onOpenStage,
}: ValidationSummaryProps) {
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
  const generated = stages.filter((s) => s.document).length;
  const allGenerated = stages.length > 0 && generated === stages.length;
  const data = summary?.data ?? null;

  const links = useMemo<StageLinks>(() => {
    const nameByType = new Map<string, string>();
    for (const stage of data?.stages ?? []) nameByType.set(stage.docType, stage.name);
    for (const stage of stages) nameByType.set(stage.docType.type, stage.docType.name);
    const generatedTypes = new Set(stages.filter((s) => s.document).map((s) => s.docType.type));
    return {
      nameOf: (docType) => nameByType.get(docType) ?? docType,
      isGenerated: (docType) => generatedTypes.has(docType),
      open: onOpenStage,
    };
  }, [data, stages, onOpenStage]);

  // Stages regenerated after the summary was built — the summary no longer reflects them.
  const staleStages = useMemo(() => {
    if (!summary || summary.status !== 'READY' || !summary.sourceUpdatedAt) return [];
    const builtAt = new Date(summary.sourceUpdatedAt).getTime();
    const sourceIds = new Set(summary.sourceDocumentIds);
    return stages.filter(
      (s) => s.document && (!sourceIds.has(s.document.id) || new Date(s.document.updatedAt).getTime() > builtAt),
    );
  }, [summary, stages]);

  if (!allGenerated) {
    return (
      <Alert severity="info">
        {generated} of {stages.length} stages generated. The summary is built once every stage has its document.
      </Alert>
    );
  }

  if (summary === undefined) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const generateButton = (label: string, icon: ReactNode) =>
    canEdit ? (
      <Button variant="contained" startIcon={icon} disabled={busy} onClick={onGenerate}>
        {label}
      </Button>
    ) : null;

  const s = copy.summary;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {summaryJob && (
        <GenerationProgressPanel job={summaryJob} title={data ? 'Updating your summary…' : 'Building your summary…'} />
      )}

      {!summaryJob && summary?.status === 'FAILED' && data && (
        <Alert severity="warning" action={generateButton('Try again', <AutorenewIcon />)}>
          The last update of the summary failed. This is the previous summary.
        </Alert>
      )}

      {!summaryJob && staleStages.length > 0 && (
        <Alert severity="warning" action={generateButton('Update summary', <AutorenewIcon />)}>
          {staleStages.length === 1 ? 'A stage was' : `${staleStages.length} stages were`} regenerated after this summary was
          built ({staleStages.map((st) => st.docType.name).join(', ')}). The summary does not reflect the new content yet.
        </Alert>
      )}

      {data && !summaryJob && canEdit && (
        <Stack direction="row" justifyContent="flex-end">
          <Button size="small" variant="outlined" startIcon={<AutorenewIcon />} disabled={busy} onClick={onGenerate}>
            Regenerate summary
          </Button>
        </Stack>
      )}

      {!data ? (
        !summaryJob && (
          <GlassPanel
            sx={{ p: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, textAlign: 'center' }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h6" fontWeight={700}>
              {summary?.status === 'FAILED' ? 'The summary could not be built' : 'Build the summary'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520 }}>
              {s.buildBody}
            </Typography>
            {generateButton(summary?.status === 'FAILED' ? 'Try again' : 'Generate summary', <AutoAwesomeIcon />)}
          </GlassPanel>
        )
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, opacity: summaryJob ? 0.55 : 1, transition: 'opacity var(--transition)' }}>
          <VerdictHero data={data} copy={copy} />
          {data.stock && <StockBlock stock={data.stock} />}
          <ScorecardBlock
            stages={data.stages}
            verdicts={data.stageVerdicts}
            takeaways={data.questionTakeaways}
            links={links}
            subtitle={s.scorecardSubtitle}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <StrengthsBlock points={data.strengths} links={links} copy={s.strengths} />
            <RisksBlock points={data.risks} links={links} copy={s.risks} />
          </Box>
          {data.metrics.length > 0 && <MetricsBlock metrics={data.metrics} links={links} />}
          {data.traction && <TractionBlock traction={data.traction} />}
          {(data.market || data.economics) && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: data.market && data.economics ? '1fr 1fr' : '1fr' }, gap: 3 }}>
              {data.market && <MarketBlock market={data.market} copy={s.market} />}
              {data.economics && <EconomicsBlock economics={data.economics} />}
            </Box>
          )}
          {(data.customer || data.competition) && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: data.customer && data.competition ? 'minmax(0, 1fr) minmax(0, 1fr)' : '1fr' }, gap: 3 }}>
              {data.customer && <CustomerBlock customer={data.customer} copy={s.customer} />}
              {data.competition && <CompetitionBlock competition={data.competition} copy={s.competition} />}
            </Box>
          )}
          {data.gtm && <GtmBlock gtm={data.gtm} copy={s.gtm} />}
          {data.deal && <DealBlock deal={data.deal} />}
          {data.experiments.length > 0 && <ExperimentsBlock experiments={data.experiments} links={links} copy={s.experiments} />}
          {summary && <DecisionPanel projectId={projectId} summary={summary} canEdit={canEdit} decisions={copy.decision} />}
          {showProjectInsights && (
            <KeyEntitiesRadarRow
              projectId={projectId}
              activeJobs={activeJobs}
              onOpenEntity={setSelectedEntity}
              allEntityTypes
            />
          )}
        </Box>
      )}
      <EntityDetailsDialog
        projectId={projectId}
        entity={selectedEntity}
        canEdit={canEdit}
        onClose={() => setSelectedEntity(null)}
      />
    </Box>
  );
}

// ── Verdict ──────────────────────────────────────────────────────────────────

function VerdictHero({ data, copy }: { data: ProjectValidationSummaryData; copy: ValidationCopy }) {
  const recommendation = copy.recommendation[data.recommendation];
  return (
    <GlassPanel sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 2, md: 3 }} alignItems={{ md: 'flex-start' }}>
        <ScoreRing score={data.overallScore} size={128} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Pill color={recommendation.color} mono={false} sx={{ fontSize: 13, px: 1.5, py: 0.5 }}>
              {RECOMMENDATION_ICON[data.recommendation]}
              {recommendation.label}
            </Pill>
            <Pill mono={false}>{CONFIDENCE_LABEL[data.confidence]}</Pill>
            {data.truncated && (
              <Pill color="warning" mono={false}>
                Partial: the summary was cut short
              </Pill>
            )}
          </Stack>
          <Typography variant="h5" component="h2" fontWeight={800} letterSpacing="-0.01em" sx={{ mt: 1.5 }}>
            {data.headline}
          </Typography>
          {data.rationale && (
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              {data.rationale}
            </Typography>
          )}
        </Box>
        <Stack
          spacing={0.75}
          sx={{
            flexShrink: 0,
            minWidth: 200,
            p: 1.5,
            borderRadius: 'var(--radius-md)',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Evidence base
          </Typography>
          <EvidenceRow value={String(data.stages.length)} label="stages generated" />
          <EvidenceRow value={String(data.evidence.questions)} label="questions answered" />
          <EvidenceRow value={String(data.evidence.sources)} label="sources cited" />
          <EvidenceRow value={String(data.evidence.assumptions)} label="assumptions flagged" />
        </Stack>
      </Stack>

      {(data.conditions.length > 0 || data.valueStatement) && (
        <>
          <Divider sx={{ my: 2.5 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: data.conditions.length > 0 && data.valueStatement ? '1fr 1fr' : '1fr' }, gap: 3 }}>
            {data.conditions.length > 0 && (
              <Box>
                <Typography variant="overline" color="text.secondary">
                  {copy.summary.conditionsTitle}
                </Typography>
                <Stack component="ol" spacing={0.75} sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                  {data.conditions.map((c) => (
                    <Typography key={c} component="li" variant="body2">
                      {c}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}
            {data.valueStatement && (
              <Box>
                <Typography variant="overline" color="text.secondary">
                  {copy.summary.statementTitle}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mt: 0.5, pl: 2, borderLeft: '3px solid var(--color-primary-line)', fontStyle: 'italic' }}
                >
                  {data.valueStatement}
                </Typography>
              </Box>
            )}
          </Box>
        </>
      )}
    </GlassPanel>
  );
}

function EvidenceRow({ value, label }: { value: string; label: string }) {
  return (
    <Stack direction="row" spacing={1} alignItems="baseline">
      <Typography variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
}

// ── Decision ─────────────────────────────────────────────────────────────────

function DecisionPanel({
  projectId,
  summary,
  canEdit,
  decisions,
}: {
  projectId: number;
  summary: ProjectValidationSummary;
  canEdit: boolean;
  decisions: ValidationCopy['decision'];
}) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState(summary.decisionNote ?? '');
  const [error, setError] = useState<string | null>(null);
  // Re-sync from the server only when another save landed (e.g. another tab), not on every render.
  const lastSaved = useRef(summary.decisionNote ?? '');
  useEffect(() => {
    const incoming = summary.decisionNote ?? '';
    if (incoming !== lastSaved.current) {
      lastSaved.current = incoming;
      setNote(incoming);
    }
  }, [summary.decisionNote]);

  const save = useMutation({
    mutationFn: (body: { decision: ValidationDecision | null; decisionNote: string | null }) =>
      resources.saveValidationDecision(projectId, body),
    onSuccess: (row) => {
      lastSaved.current = row.decisionNote ?? '';
      queryClient.setQueryData(['validation-summary', projectId], row);
      setError(null);
    },
    onError: (e) => setError(extractErrorMessage(e)),
  });

  // Debounced note save, only once a decision exists (a note without a decision has nothing to attach to).
  useEffect(() => {
    if (!canEdit || !summary.decision) return;
    const trimmed = note.trim();
    if (trimmed === (lastSaved.current ?? '').trim()) return;
    const timer = setTimeout(() => save.mutate({ decision: summary.decision, decisionNote: trimmed || null }), NOTE_SAVE_DELAY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note, summary.decision, canEdit]);

  const decide = (decision: ValidationDecision) => {
    const next = summary.decision === decision ? null : decision;
    save.mutate({ decision: next, decisionNote: next ? note.trim() || null : null });
  };

  const decided = summary.decision ? decisions[summary.decision] : null;
  const decidedAt = summary.decidedAt ? new Date(summary.decidedAt).toLocaleDateString() : null;

  return (
    <GlassPanel sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ mb: 2 }}>
        <IconTile icon={<GavelOutlinedIcon fontSize="inherit" />} color="primary" size="sm" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
            Your decision
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {decided
              ? `Recorded${decidedAt ? ` on ${decidedAt}` : ''}. It stays with the project when the summary is updated.`
              : 'Record where you land after reading the summary. It stays with the project.'}
          </Typography>
        </Box>
        {save.isPending && <CircularProgress size={18} />}
      </Stack>

      {canEdit ? (
        <>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {DECISIONS.map((d) => {
              const display = decisions[d.key];
              const selected = summary.decision === d.key;
              const tone: SemanticColor = display.color;
              return (
                <SoftButton
                  key={d.key}
                  tone={tone}
                  size="large"
                  startIcon={d.icon}
                  aria-pressed={selected}
                  disabled={save.isPending}
                  onClick={() => decide(d.key)}
                  sx={selected ? { bgcolor: `var(--color-${tone})`, color: '#fff', borderColor: `var(--color-${tone})` } : undefined}
                >
                  {display.label}
                </SoftButton>
              );
            })}
          </Stack>
          <TextField
            label="Why, and what happens next"
            placeholder="What convinced you, what you still doubt, and the first thing you will do."
            helperText={summary.decision ? undefined : 'Pick a decision first; the note is saved with it.'}
            multiline
            minRows={3}
            fullWidth
            value={note}
            onChange={(e) => setNote(e.target.value)}
            sx={{ mt: 2 }}
          />
        </>
      ) : decided ? (
        <Box>
          <Pill color={decided.color} mono={false} sx={{ fontSize: 13, px: 1.5, py: 0.5 }}>
            {decided.label}
          </Pill>
          {summary.decisionNote && (
            <Typography variant="body2" sx={{ mt: 1.5, whiteSpace: 'pre-wrap' }}>
              {summary.decisionNote}
            </Typography>
          )}
        </Box>
      ) : (
        <Typography variant="body2" color="text.disabled">
          No decision recorded yet.
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
    </GlassPanel>
  );
}
